/**
 * Broker Integration Repository
 * Handles all Firestore operations for broker integrations
 */

import { db } from '../services/firebase';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { cleanObjectForFirestore } from '../utils/firestoreHelpers';

const COLLECTION_NAME = 'tbl_broker_integration';

/**
 * Subscribe to user's broker integrations
 * @param {string} userId - User ID
 * @param {function} onData - Callback for data updates
 * @param {function} onError - Callback for errors
 * @returns {function} Unsubscribe function
 */
const subscribeToUserIntegrations = (userId, onData, onError) => {
  if (!userId) {
    onError(new Error('User ID is required'));
    return () => {};
  }

  const q = query(
    collection(db, COLLECTION_NAME),
    where('userId', '==', userId)
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const integrations = [];
      snapshot.forEach((doc) => {
        integrations.push({
          id: doc.id,
          ...doc.data()
        });
      });
      onData(integrations);
    },
    onError
  );
};

/**
 * Get single integration by ID
 * @param {string} userId - User ID
 * @param {string} integrationId - Integration ID
 * @returns {Promise<object|null>} Integration data or null
 */
const getIntegration = async (userId, integrationId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, integrationId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();

    // Verify ownership
    if (data.userId !== userId) {
      throw new Error('Unauthorized access to integration');
    }

    return {
      id: docSnap.id,
      ...data
    };
  } catch (error) {
    console.error('Error getting integration:', error);
    throw error;
  }
};

/**
 * Get all integrations for a user
 * @param {string} userId - User ID
 * @returns {Promise<array>} Array of integrations
 */
const getUserIntegrations = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    const integrations = [];

    querySnapshot.forEach((doc) => {
      integrations.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return integrations;
  } catch (error) {
    console.error('Error getting user integrations:', error);
    throw error;
  }
};

/**
 * Create new broker integration
 * @param {string} userId - User ID
 * @param {string} broker - Broker identifier
 * @param {object} credentials - Broker credentials
 * @param {object} userProfile - User profile data
 * @returns {Promise<string>} Integration ID
 */
const createIntegration = async (userId, broker, credentials, userProfile) => {
  try {
    // Create unique ID for this integration
    const integrationId = `${userId}_${broker}`;

    // Check if integration already exists
    const existingIntegrations = await getUserIntegrations(userId);
    const existing = existingIntegrations.filter(i => i.broker === broker);

    // Delete existing integrations for this broker
    if (existing.length > 0) {
      await Promise.all(
        existing.map(integration =>
          deleteDoc(doc(db, COLLECTION_NAME, integration.id))
        )
      );
      console.log(`Removed ${existing.length} old ${broker} integration(s)`);
    }

    // Prepare integration data
    const integrationData = {
      username: userProfile?.username || 'unknown',
      userId: userId,
      broker: broker,
      credentials: {
        ...credentials,
        // Ensure required fields
        apiKey: credentials?.apiKey || null,
        apiSecret: credentials?.apiSecret || null,
        accountId: credentials?.accountId || null,
        accessToken: credentials?.accessToken || null,
        refreshToken: credentials?.refreshToken || null,
        userEmail: credentials?.userEmail || userProfile?.email || 'unknown@email.com',
        userName: credentials?.userName || userProfile?.username || 'Unknown User',
        userUsername: credentials?.userUsername || userProfile?.username || 'unknown',
        expiresAt: credentials?.expiresAt || null,
        sessionExpiration: credentials?.sessionExpiration || null
      },
      status: 'active',
      connectionDate: serverTimestamp(),
      lastSync: serverTimestamp(),
      metadata: {
        accountType: credentials.accountType || credentials.metadata?.accountType || 'live',
        accountName: credentials.accountName || credentials.userName || `${broker} Account`,
        accountBalance: null,
        isVerified: credentials.metadata?.isVerified || false,
        lastLogin: credentials.metadata?.lastLogin || serverTimestamp(),
        brokerDisplayName: credentials.brokerDisplayName ||
          broker.charAt(0).toUpperCase() + broker.slice(1),
        connectedUsername: credentials.metadata?.connectedUsername ||
          credentials.userUsername || userProfile?.username,
        expirationDate: credentials.metadata?.expirationDate ||
          credentials.expiresAt || credentials.sessionExpiration || null,
        selectedAccount: credentials.metadata?.selectedAccount ||
          credentials.accountNumber || credentials.accountId || null,
        selectedAccountData: credentials.metadata?.selectedAccountData || null,
        lastAccountRefresh: credentials.metadata?.lastAccountRefresh || null
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Clean and save
    const cleanedData = cleanObjectForFirestore(integrationData);
    await setDoc(doc(db, COLLECTION_NAME, integrationId), cleanedData);

    console.log('✅ Broker integration created:', integrationId);
    return integrationId;
  } catch (error) {
    console.error('Error creating integration:', error);
    throw error;
  }
};

/**
 * Update broker integration
 * @param {string} userId - User ID
 * @param {string} integrationId - Integration ID
 * @param {object} updates - Updates to apply
 * @returns {Promise<boolean>} Success status
 */
const updateIntegration = async (userId, integrationId, updates) => {
  try {
    const integrationRef = doc(db, COLLECTION_NAME, integrationId);

    // Verify ownership
    const integrationDoc = await getDoc(integrationRef);
    if (!integrationDoc.exists() || integrationDoc.data().userId !== userId) {
      throw new Error('Unauthorized to update this integration');
    }

    // Clean and update
    const cleanedUpdates = cleanObjectForFirestore({
      ...updates,
      updatedAt: serverTimestamp()
    });

    await updateDoc(integrationRef, cleanedUpdates);
    console.log('✅ Integration updated:', integrationId);
    return true;
  } catch (error) {
    console.error('Error updating integration:', error);
    throw error;
  }
};

/**
 * Delete broker integration
 * @param {string} userId - User ID
 * @param {string} integrationId - Integration ID
 * @returns {Promise<boolean>} Success status
 */
const deleteIntegration = async (userId, integrationId) => {
  try {
    const integrationRef = doc(db, COLLECTION_NAME, integrationId);

    // Verify ownership
    const integrationDoc = await getDoc(integrationRef);
    if (!integrationDoc.exists() || integrationDoc.data().userId !== userId) {
      throw new Error('Unauthorized to delete this integration');
    }

    await deleteDoc(integrationRef);
    console.log('✅ Integration deleted:', integrationId);
    return true;
  } catch (error) {
    console.error('Error deleting integration:', error);
    throw error;
  }
};

/**
 * Clean up disconnected broker integrations
 * ลบ documents ที่มี status = 'disconnected' ออกจาก tbl_broker_integration
 * @returns {Promise<object>} Cleanup result
 */
const cleanupDisconnectedIntegrations = async () => {
  try {
    console.log('🧹 Starting cleanup of disconnected broker integrations...');
    
    // Query documents with status = 'disconnected'
    const q = query(
      collection(db, COLLECTION_NAME),
      where('status', '==', 'disconnected')
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.log('✅ No disconnected broker integrations found.');
      return { success: true, deleted: 0 };
    }
    
    console.log(`🔍 Found ${snapshot.size} disconnected broker integrations to delete`);
    
    // Delete all disconnected integrations
    const deletePromises = snapshot.docs.map(docSnapshot => {
      const data = docSnapshot.data();
      console.log(`🗑️ Deleting: ${docSnapshot.id} (${data.broker} - ${data.userId})`);
      return deleteDoc(docSnapshot.ref);
    });
    
    await Promise.all(deletePromises);
    
    console.log(`🎉 Cleanup complete! Deleted ${snapshot.size} disconnected broker integrations`);
    
    return { 
      success: true, 
      deleted: snapshot.size,
      message: `Successfully deleted ${snapshot.size} disconnected broker integrations`
    };
    
  } catch (error) {
    console.error('❌ Error during disconnected broker cleanup:', error);
    return { 
      success: false, 
      deleted: 0,
      error: error.message 
    };
  }
};

/**
 * Check if user has active integration for broker
 * @param {string} userId - User ID
 * @param {string} broker - Broker identifier
 * @returns {Promise<boolean>} Has active integration
 */
const hasActiveIntegration = async (userId, broker) => {
  try {
    const integrations = await getUserIntegrations(userId);
    return integrations.some(i =>
      i.broker === broker &&
      (i.status === 'active' || i.status === 'connected')
    );
  } catch (error) {
    console.error('Error checking active integration:', error);
    return false;
  }
};

export default {
  subscribeToUserIntegrations,
  getIntegration,
  getUserIntegrations,
  createIntegration,
  updateIntegration,
  deleteIntegration,
  hasActiveIntegration,
  cleanupDisconnectedIntegrations
};